/**
 * ComplaintCreatePage — Issue #175.
 *
 * Admin form for creating a complaint / dispute work order.
 *
 * 红线：
 *   - 通过 useComplaints hook 调用生成的 typed client；不允许伪造 ID。
 *   - 创建成功后跳转详情页；不在前端伪造 status 等业务字段。
 */
import { useState, type FormEvent } from 'react';
import { useLocation } from 'wouter';
import { toast } from 'sonner';
import AccessDenied from '@/components/AccessDenied';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import {
  AssociationLookupSelector,
  type SelectedAssociation,
} from '@/components/business/AssociationLookupSelector';
import {
  COMPLAINT_PERMISSIONS,
  hasPermission,
} from '@/lib/complaintPermissions';
import {
  useComplaints,
  COMPLAINT_RISK_LABELS,
  COMPLAINT_RISK_LIST,
  COMPLAINT_TYPE_LABELS,
  COMPLAINT_TYPE_LIST,
  type ComplaintWorkOrderRiskLevel,
  type ComplaintWorkOrderType,
} from '@/hooks/useComplaints';

export default function ComplaintCreatePage() {
  const { permissions } = useAuth();
  const canCreate = hasPermission(permissions, COMPLAINT_PERMISSIONS.CREATE);
  const [, navigate] = useLocation();
  const { createComplaint, error } = useComplaints();
  const [submitting, setSubmitting] = useState(false);

  const [customer, setCustomer] = useState<SelectedAssociation | null>(null);
  const [orderId, setOrderId] = useState('');
  const [orderProjectId, setOrderProjectId] = useState('');
  const [serviceUserId, setServiceUserId] = useState('');
  const [complaintType, setComplaintType] =
    useState<ComplaintWorkOrderType>('service_quality');
  const [riskLevel, setRiskLevel] =
    useState<ComplaintWorkOrderRiskLevel>('medium');
  const [title, setTitle] = useState('');
  const [customerDemand, setCustomerDemand] = useState('');
  const [description, setDescription] = useState('');
  const [owner, setOwner] = useState<SelectedAssociation | null>(null);
  const [assignee, setAssignee] = useState<SelectedAssociation | null>(null);
  const [slaDueAt, setSlaDueAt] = useState('');

  if (!canCreate) {
    return <AccessDenied resource="投诉与纠纷工单" />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!customer?.id || !title.trim() || !customerDemand.trim()) {
      toast.error('请选择客户并填写标题与客户诉求');
      return;
    }
    setSubmitting(true);
    const result = await createComplaint({
      customerId: customer.id,
      orderId: orderId.trim() || undefined,
      orderProjectId: orderProjectId.trim() || undefined,
      serviceUserId: serviceUserId.trim() || undefined,
      complaintType,
      riskLevel,
      title: title.trim(),
      customerDemand: customerDemand.trim(),
      description: description.trim() || undefined,
      ownerId: owner?.id ?? undefined,
      assigneeId: assignee?.id ?? undefined,
      slaDueAt: slaDueAt ? new Date(slaDueAt).toISOString() : undefined,
    });
    setSubmitting(false);
    if (result) {
      toast.success('投诉工单已登记');
      navigate(`/complaints/${result.id}`);
    } else if (error) {
      toast.error(`登记失败：${error.message}`);
    }
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <a href="/complaints" className="hover:underline">
          投诉与纠纷工单
        </a>
        <span>/</span>
        <span className="text-foreground">新建</span>
      </div>

      <h1 className="text-xl font-semibold">新建投诉/纠纷工单</h1>

      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="customerId">
                客户 <span className="text-red-500">*</span>
              </Label>
              <AssociationLookupSelector
                id="customerId"
                kind="customer"
                value={customer}
                onChange={setCustomer}
                placeholder="按姓名搜索客户…"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="orderId">订单 ID（可选）</Label>
              <Input
                id="orderId"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="如果与某订单相关"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="orderProjectId">订单项目 ID（可选）</Label>
              <Input
                id="orderProjectId"
                value={orderProjectId}
                onChange={(e) => setOrderProjectId(e.target.value)}
                placeholder="如与具体项目相关"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="serviceUserId">服务用户 ID（可选）</Label>
              <Input
                id="serviceUserId"
                value={serviceUserId}
                onChange={(e) => setServiceUserId(e.target.value)}
                placeholder="家属/服务对象 ID"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="complaintType">投诉类型</Label>
              <select
                id="complaintType"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={complaintType}
                onChange={(e) =>
                  setComplaintType(e.target.value as ComplaintWorkOrderType)
                }
              >
                {COMPLAINT_TYPE_LIST.map((t) => (
                  <option key={t} value={t}>
                    {COMPLAINT_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="riskLevel">风险等级</Label>
              <select
                id="riskLevel"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={riskLevel}
                onChange={(e) =>
                  setRiskLevel(e.target.value as ComplaintWorkOrderRiskLevel)
                }
              >
                {COMPLAINT_RISK_LIST.map((r) => (
                  <option key={r} value={r}>
                    {COMPLAINT_RISK_LABELS[r]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="title">
              标题 <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="简要描述投诉/纠纷"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="customerDemand">
              客户诉求 <span className="text-red-500">*</span>
            </Label>
            <textarea
              id="customerDemand"
              className="min-h-[100px] w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={customerDemand}
              onChange={(e) => setCustomerDemand(e.target.value)}
              placeholder="客户希望的处理结果或赔付"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description">备注（可选）</Label>
            <textarea
              id="description"
              className="min-h-[80px] w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="内部说明"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="ownerId">负责人（可选）</Label>
              {/*
                complaint_work_orders.owner_id / assignee_id are system user
                IDs. kind="user" returns users.id so the work-order
                ownership flow keeps working; kind="employee" would write
                an hr_employees.id and break user-scoped queries / reassign
                paths. Issue #344 P1 fix.
              */}
              <AssociationLookupSelector
                id="ownerId"
                kind="user"
                value={owner}
                onChange={setOwner}
                placeholder="搜索负责人姓名…"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="assigneeId">处理人（可选）</Label>
              <AssociationLookupSelector
                id="assigneeId"
                kind="user"
                value={assignee}
                onChange={setAssignee}
                placeholder="搜索处理人姓名…"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="slaDueAt">SLA 到期时间（可选）</Label>
              <Input
                id="slaDueAt"
                type="datetime-local"
                value={slaDueAt}
                onChange={(e) => setSlaDueAt(e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/complaints')}
            >
              取消
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? '提交中…' : '登记投诉'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
